import { useCallback, useRef } from 'react';
import { notifications } from '@mantine/notifications';
import confetti from 'canvas-confetti';

import { useStylesNotification } from './useStylesNotification';

// Verrou global pour éviter les notifications simultanées identiques
const pendingNotifications = new Set<string>();

export const useNotification = () => {
    const { classes } = useStylesNotification();
    const classesRef = useRef(classes);

    classesRef.current = classes;

    return useCallback((type: 'success' | 'error' | 'warning' | 'info', message: string, autoClose = 5000, withConfetti = false) => {
        // Créer un ID unique basé sur type + message
        const notificationId = `notif-${type}-${message.replace(/[^a-zA-Z0-9]/g, '-')}`;

        // Si cette notification est déjà en cours de traitement, ignorer
        if (pendingNotifications.has(notificationId)) {
            return;
        }

        // Marquer comme en cours
        pendingNotifications.add(notificationId);

        let color = '';
        switch (type) {
            case 'success':
                color = 'var(--mantine-color-nli-success-filled)';
                break;
            case 'error':
                color = 'var(--mantine-color-nli-error-filled)';
                break;
            case 'warning':
                color = 'var(--mantine-color-nli-warning-filled)';
                break;
            case 'info':
                color = 'var(--mantine-color-nli-pink-lavender-filled)';
                break;
        }

        // Afficher la notification avec un ID unique
        notifications.show({
            id: notificationId,
            message,
            color,
            autoClose,
            withBorder: true,
            classNames: classesRef.current,
            onClose: () => {
                // Retirer du verrou quand la notification se ferme
                pendingNotifications.delete(notificationId);
            },
        });

        // Retirer du verrou après un court délai (au cas où onClose ne serait pas appelé)
        setTimeout(() => {
            pendingNotifications.delete(notificationId);
        }, autoClose || 5000);

        if (withConfetti) {
            setTimeout(() => {
                void confetti({
                    particleCount: 50,
                    angle: 90,
                    spread: 60,
                    startVelocity: 35,
                    origin: { x: 0.5, y: 1 },
                    colors: ['#1d193a', '#5b66ad', '#f2f6f9', '#c9a1c4'],
                });
            }, 150);
        }
    }, []);
};